/**
 * @title Country selection list
 * @description Shows a widget with a list of countries
 */
import * as React from "react";
import {Controller} from "react-hook-form";
import {makeStyles} from "@mui/styles";
import {FormControl, InputLabel, MenuItem, Select, SelectProps, useMediaQuery} from "@mui/material";
import {useRecoilValue} from "recoil";
import {LocaleAtom, LocalizationsAtom} from "../providers/localization/LocalizationProvider";
import {CountryMapFactory} from "../utility/CountryMapFactory";

let nextId = 0;

export type FormInputCountryProps = SelectProps & {
   name: string;
   control: any;
   label: string;
   setValue?: any;
   errorMessage: string
}

export const FormInputCountry: React.FC<FormInputCountryProps & { countries: string[] }> = ({
                                                                                               name,
                                                                                               control,
                                                                                               label,
                                                                                               countries,
                                                                                               errorMessage,
                                                                                               onChange: onExtChange,
                                                                                            }) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const locale = useRecoilValue(LocaleAtom);

   const isDesktop = useMediaQuery('(min-width:800px)');

   let labelid = "simple-select-" + ++nextId;
   const classes = useStyles();

   // Sort the countries based on their localized names
   const sortedCountries = countries
      .slice() // Create a shallow copy of the countries array
      .sort((a, b) => {
         const nameA = localizations.getString('profileCountry_' + a);
         const nameB = localizations.getString('profileCountry_' + b);
         return nameA.localeCompare(nameB, locale); // Sort by localized names
      });

   return (
      <FormControl fullWidth variant={"filled"} margin={"none"} size={"small"}>
         <InputLabel sx={{zIndex: '0'}} shrink={true} id={labelid}>
            {label}
         </InputLabel>
         <Controller
            render={({field: {onChange, value}, fieldState: {error}}) => {
               return (
                  <>
                     <Select
                        MenuProps={{classes: {paper: classes.dropdownStyle}}}
                        style={{
                           backgroundColor: 'transparent',
                           border: '1px solid #d0d0d0',
                           borderRadius: 2,
                           flexGrow: 1,
                        }}
                        error={!!error}
                        disableUnderline={true}
                        labelId={labelid}
                        renderValue={(value) => {
                           const title = localizations.getString('profileCountry_' + value);
                           return title || CountryMapFactory.getCountry(value)?.name || value;
                        }}
                        onChange={(e, v) => {
                           onChange(e);
                           onExtChange && onExtChange(e, v);
                        }}
                        value={value || ''}
                        label={label}
                     >
                        {sortedCountries.map((country) => {
                           const countryInfo = CountryMapFactory.getCountry(country);

                           const title = localizations.getString('profileCountry_' + country);

                           const flagEmoji = countryInfo?.emoji; // Use the emoji from the country data
                           const flagSvg = countryInfo?.svg

                           return (
                              <MenuItem value={country} dense={true} key={country} style={{verticalAlign: 'center'}}>
                                 <span style={{
                                    marginRight: 8,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '50px',
                                    padding: '0px 4px 0px 4px'
                                 }}>
                                    {isDesktop && <img
                                       src={flagSvg}
                                       alt={`${title} flag`}
                                       style={{position: 'relative', top: '2px', width: '1em', height: '1em', borderRadius: '50%'}}
                                    />
                                    }
                                    {!isDesktop && flagEmoji}
                                 </span>
                                 {title}
                              </MenuItem>
                           );
                        })}
                     </Select>
                  </>
               );
            }}
            control={control}
            name={name}
         />
      </FormControl>
   );
};

const useStyles = makeStyles({
   dropdownStyle: {
      padding: 0,
   },
});
